/**
 * Created by simon zhao on 4/24/2022.
 * Description:
 * DiagramNoteAutomationDialog Component
 * ------ maintenance history ------
 * Updated by Daniel Wang on 5/30/2023, to support add sidenote
 * Updated by Daniel Wang on 7/3/2023, to support edit note
 */
import { KeyValue } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { FieldConfig } from '../../../../components/work-flow/field/field.model';
import { businessConstants } from '../../../../constants/business.constants';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';
import { AppState } from '../../../../redux';
import { StoreQuerierService } from '../../../../services/store-querier.service';
import { AdhocWorkflowField } from '../../../../tamalelibs/models/adhoc-field.model';
import { AutomationNoteMappingModel, DEFAULT_METDATA_OF_NOTE, OWNER_PREFIX } from '../../../../tamalelibs/models/automation.model';
import { EntryType } from '../../../../tamalelibs/models/entry-type.model';
import { ControlType } from '../../../../tamalelibs/models/template-control.model';
import { WorkflowTriggerSource } from '../../../../tamalelibs/models/workflow-config.model';
import { currentWorkflowAllFieldsSelector } from '../../../../tamalelibs/redux/reducers/workflow.reducer';
import { AdhocService } from '../../../../tamalelibs/services/adhoc.service';
import { ArrayHelperService } from '../../../../tamalelibs/services/array-helper.service';
import { WorkflowConfigService } from '../../../../tamalelibs/services/workflow-config.service';
import { WorkflowService } from '../../../../tamalelibs/services/workflow.service';
import { AlertWindowService } from '../../../alert-window/alert-window.service';
import { MultiSelDropdownFilterConfig } from '../../../multi-sel-dropdown-filter/multi-sel-dropdown-filter.model';
import { AutomationActionType } from '../../diagram-context-menu/diagram-context-menu.model';
import { AutomationMappingItemViewModel, DiagramNoteAutomationDialogOptions, DiagramNoteAutomationDialogResult } from './diagram-note-automation-dialog.model';
import { DiagramAutomationNoteDialogService } from './diagram-note-automation-dialog.service';

import * as _lodash from 'lodash';
import { WorkflowActionTypes } from '../../../../tamalelibs/redux/actions/workflow.actions';

@Component({
    selector: 'tam-diagram-note-automation-dialog',
    templateUrl: './diagram-note-automation-dialog.component.html',
    styleUrls: ['./diagram-note-automation-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DiagramNoteAutomationDialogComponent implements OnInit, OnDestroy {
    //#region public properties
    /**
     * all workflow fields
     */
    allFieldControls: Array<FieldConfig> = [];
    /**
     * all adhoc fields associated with current note type.
     */
    adhocFieldsOfCurrentNoteType: Array<AdhocWorkflowField> = [];
    /**
     * the input parameters for current dialog instance.
     */
    dialogOptions: DiagramNoteAutomationDialogOptions;
    /**
     * the current dialog title
     */
    diglogTitle: string;
    /**
     * the data source of note type dropdown list.
     */
    filteredNoteTypeCandidates: Array<EntryType> = [];
    /**
     * a flag indicating whether the save button is clicked after any change is made.
     */
    isSaveBtnClicked: boolean;
    /**
     * a flag indicating whether the dialog is loading.
     */
    isDialogLoading: boolean;
    /**
     * an array of mapping items.
     */
    mappingMetadataList: Array<AutomationMappingItemViewModel> = [];
    /**
     * a flag indicating whether current dialog is visible.
     */
    opened: boolean;
    /**
     * current selected note type.
     */
    selectedNoteType: EntryType;
    /**
     * a list of submitters of all workflow tasks.
     */
    submitterOfTaskList: Array<KeyValue<string, string>> = [];
    /**
     * an array of unmapped template fields.
     */
    templateFieldCandidates: Array<AutomationMappingItemViewModel> = [];
    /**
     * a flag indicating whether the note type is selected and the mapping list has been initialized.
     */
    get isLoaded(): boolean {
        return this.selectedNoteType && this.mappingMetadataList && this.mappingMetadataList.length >= 0;
    }
    /**
     * a flag indicating whether current configuration is valid.
     */
    get isMappingValid() {
        const isSingleMappingValidFunc = (sItem: AutomationMappingItemViewModel) => {
            return !!sItem.fieldInTemplate && !!sItem.fieldsInWorkflow && (!Array.isArray(sItem.fieldsInWorkflow) || Array.from(sItem.fieldsInWorkflow).length > 0);
        };
        const isMultipleMappingValidFunc = (item: AutomationMappingItemViewModel) => {
            return !!item.dataSourceForMultipleSelection && !!item.dataSourceForMultipleSelection.data && item.dataSourceForMultipleSelection.data.some(dItem => dItem.checked);
        };
        return !!this.selectedNoteType && this.mappingMetadataList.length > 0 && this.mappingMetadataList.every(it => it.isMultipleSelection ? isMultipleMappingValidFunc(it) : isSingleMappingValidFunc(it));
    }
    get isUpdateANote(): boolean {
        return this.dialogOptions.type === AutomationActionType.UpdateANote;
    }
    /**
     * a flag indicating whether current dialog is for note updating.
     */
    get isUpdating(): boolean {
        return this.dialogOptions && this.dialogOptions.isUpdate;
    }
    //#endregion

    //#region private properties
    private _allNoteTypes: Array<EntryType> = [];
    /**
    * the subscription array that would be unsubscribed on destroying.
    */
    private _destroySubscriptions: Array<Subscription> = [];
    /**
     * the subscription array that would be unsubscribed on closing.
     */
    private _openSubscriptions: Array<Subscription> = [];
    /**
     * a flag indicating whether the dialog content is pristine.
     */
    private _isDialogPristine = true;
    //#endregion

    //#region constructor
    constructor(
        private _adhocService: AdhocService,
        private _alertWindow: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _diagramAutomationDialogService: DiagramAutomationNoteDialogService,
        private _dialogService: DiagramAutomationNoteDialogService,
        private _storeQueriery: StoreQuerierService,
        private _store: Store<AppState>,
        private _workflowService: WorkflowService,
        private _workflowConfigService: WorkflowConfigService) { }
    // #endregion

    //#region public methods
    ngOnInit() {
        this._destroySubscriptions.push(
            this._dialogService.dialogOpen$.pipe(filter(() => !this.opened),
            ).subscribe((dialogOptions: DiagramNoteAutomationDialogOptions) => {
                this.dialogOptions = dialogOptions;
                this._reset();
                this._initDialogProperties();
                this._initCandidates();
                this.opened = true;
                this._changeDetectorRef.detectChanges();
            }));
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeunloadHandler($event: any) {
        if (this.opened) {
            $event.returnValue = true;
        }
    }

    /**
     * event handler for the click event of cancel buttonn;
     * @param $event the event argument
     */
    onCancelBtnClick($event: Event) {
        if ($event.stopPropagation) {
            $event.stopPropagation();
        }

        if (!this._isDialogPristine) {
            // pop up the confirmation dialog only when the dialog content has been changed.
            const subscription = this._alertWindow.warn(businessConstants.alertWindow.warnTitle, [businessConstants.alertWindow.warnContent])
                .subscribe((result: boolean) => {
                    if (result) {
                        this._close();
                    }
                    subscription.unsubscribe();
                });
        } else {
            this._close();
        }
    }

    /**
     * event handler for the click event of save button.
     * @param e event argument.
     */
    onClickSaveBtn(e: Event) {
        this.isSaveBtnClicked = true;
        if (this.isMappingValid) {
            let automationRecord = new AutomationNoteMappingModel();
            automationRecord = this._generateAutomationRecord();
            let noteTypes = Array<EntryType>();
            this._diagramAutomationDialogService.getNoteTypes().pipe(take(1)).subscribe(res => {
                noteTypes = Array.from(res.entryTypes);
            });
            automationRecord.name = this._workflowService.getAutomationCardContent(automationRecord, noteTypes);
            automationRecord.enable = true;
            automationRecord.attachedNodeId = this.dialogOptions.nodeId;
            // add the new automation node to store.
            if (automationRecord) {
                if (this.isUpdating) {
                    this._store.dispatch({
                        type: WorkflowActionTypes.UPDATE_AUTOMATION_NODE,
                        payload: [automationRecord]
                    });
                } else {
                    this._store.dispatch({
                        type: WorkflowActionTypes.ADD_AUTOMATION_NODE,
                        payload: [automationRecord]
                    });
                }
            }
            this._close(automationRecord);
        }
    }

    /**
     * event handler for the filter change event of note type dropdown.
     * @param keywords the characters user typed in.
     * @param dropDownEl the dropdown element reference.
     */
    onFilterChange(keywords: string, dropDownEl) {
        this.filteredNoteTypeCandidates = [];
        if (!keywords) {
            this.filteredNoteTypeCandidates = this._allNoteTypes.slice();
        } else {
            this.filteredNoteTypeCandidates = this._allNoteTypes.filter((s) => s.name.toLowerCase().indexOf(keywords.toLowerCase()) >= 0);

            if (this.filteredNoteTypeCandidates.length >= 1) {
                setTimeout(() => {
                    dropDownEl.optionsList.content.nativeElement.scrollTop = 0;
                    this._changeDetectorRef.detectChanges();
                });
            }
        }
        this._changeDetectorRef.detectChanges();
    }

    /**
     * event handler for the value change event of note type dropdown list.
     * @param noteType the new selected note type.
     */
    onNoteTypeChange(noteType: EntryType) {
        this.mappingMetadataList = [];
        this._getNoteTypeRelatedMappingConfigs(noteType).subscribe(vmArray => {
            // init the mapping list with default required meta data items.
            this.mappingMetadataList.push(...vmArray.filter(vm => vm.isPreloaded));
            if (this.dialogOptions.type === AutomationActionType.CreateASidenote) {
                this.mappingMetadataList = this.mappingMetadataList.filter(item => item.fieldInTemplate !== 'Display Date');
            }
            // put those optional meta data items and adhoc fields into the candidate list.
            this._refreshTemplateFieldCandidates(noteType);
        });
        this._afterDialogChange();
    }

    /**
     * removes the given mapping item from the list.
     * @param metaItem the abandoned mapping item.
     */
    onDeleteMetaItem(metaItem: AutomationMappingItemViewModel) {
        if (metaItem && this.mappingMetadataList.includes(metaItem)) {
            this.mappingMetadataList.splice(this.mappingMetadataList.indexOf(metaItem), 1);
        }
        this._refreshTemplateFieldCandidates();
        this._afterDialogChange();
    }

    /**
     * the event handler for the value change of mapping items.
     * @param e event argument
     */
    onMetaItemFieldsChange(e: any) {
        this._afterDialogChange();
    }

    /**
     * the event handler for the selection change of the candidate list.
     * @param newItem the selected candidate item
     */
    onFieldCandidateSelected(newItem: AutomationMappingItemViewModel) {
        const copyOfNewItem = new AutomationMappingItemViewModel();
        Object.assign(copyOfNewItem, newItem);
        copyOfNewItem.dataSourceForSingleSelection = this._generateDataSourceForMappingItem(copyOfNewItem.applicableFieldTypes, newItem.isTaskSubmitterIncluded, copyOfNewItem.applicableSystemVariables);
        copyOfNewItem.dataSourceForMultipleSelection = this._wrapDataSourceForMultipleSelection(copyOfNewItem.applicableFieldTypes, newItem.isTaskSubmitterIncluded, copyOfNewItem.applicableSystemVariables);
        this.mappingMetadataList.push(copyOfNewItem);
        this._refreshTemplateFieldCandidates();
        this._afterDialogChange();
    }

    //#endregion

    //#region private methods
    private _afterDialogChange() {
        this._isDialogPristine = false;
        this.isSaveBtnClicked = false;
    }
    /**
     * load the saved record.
     */
    private _afterNoteCandidatesInit() {
        if (this.dialogOptions) {
            if (this.dialogOptions.isUpdate && this.dialogOptions.existingMapping) {
                const nType = new EntryType();
                nType.id = this.dialogOptions.existingMapping.objectTypeID;
                this.selectedNoteType = nType;
                this._getNoteTypeRelatedMappingConfigs(this.selectedNoteType).subscribe(vmArray => {
                    this.mappingMetadataList = DiagramNoteAutomationDialogOptions.toViewModelArray(this.dialogOptions, vmArray);
                    this._refreshTemplateFieldCandidates(this.selectedNoteType);
                    this._changeDetectorRef.detectChanges();
                });
            } else {
                if (this.dialogOptions.type === AutomationActionType.UpdateANote) {
                    // get trigger note type
                    const triggerSetting = this._workflowConfigService.getWorkflowTriggerSettingConfig;
                    if (triggerSetting && triggerSetting.triggerSource && triggerSetting.triggerSource.length > 0) {
                        const triggerNoteType = new EntryType();
                        const workflowTriggerSource: WorkflowTriggerSource = triggerSetting.triggerSource[0];
                        triggerNoteType.id = workflowTriggerSource.objectTypeID;
                        this.selectedNoteType = triggerNoteType;
                        this._getNoteTypeRelatedMappingConfigs(this.selectedNoteType).subscribe(vmArray => {
                            this.mappingMetadataList = DiagramNoteAutomationDialogOptions.toViewModelArray(this.dialogOptions, vmArray);
                            // put those optional meta data items and adhoc fields into the candidate list.
                            this._refreshTemplateFieldCandidates(this.selectedNoteType);
                            this._changeDetectorRef.detectChanges();
                        });
                    } else {
                        console.error('can not get the trigger source');
                    }
                }
            }
        } else {
            console.error('the parameter dialogOptions is lost when opening the automation note dialog');
        }
    }
    /**
     * generates a data source list for the given meta data item.
     * @param applicableFieldTypes applicable workflow field types
     * @param isSubmitterIncluded a flag indicating whether the submitters of workflow tasks should be part of the returned list.
     * @param applicableSysVars optional, a key value pair list, each of them represents a system variable, to which the field would map.
     * @returns a list of workflow fields for mapping to the given meta data item.
     */
    private _generateDataSourceForMappingItem(applicableFieldTypes: Array<ControlType>, isSubmitterIncluded = false, applicableSysVars?: Array<KeyValue<string, string>>) {
        const fields = this.allFieldControls.filter(fld => applicableFieldTypes.length === 0 || applicableFieldTypes.includes(fld.type)).map(fldCfg => ({ key: fldCfg.label, value: fldCfg.fieldDefinitionId }));
        if (isSubmitterIncluded && this.submitterOfTaskList.length > 0) {
            fields.push(... this.submitterOfTaskList);
        }
        if (applicableSysVars) {
            applicableSysVars.forEach(
                sysVar => {
                    const displayText = StringLiteralsPipe.translate(sysVar.key);
                    fields.push({ key: displayText, value: sysVar.value });
                }
            );
        }

        return fields;
    }

    private _wrapDataSourceForMultipleSelection(applicableFieldTypes: Array<ControlType>, isSubmitterIncluded = false, applicableSysVars?: Array<KeyValue<string, string>>) {
        const ds = new MultiSelDropdownFilterConfig();
        const fields = this._generateDataSourceForMappingItem(applicableFieldTypes, isSubmitterIncluded, applicableSysVars);
        ds.data = fields.map(f => ({ id: f.value, name: f.key, checked: false }));
        return ds;
    }

    /**
     * generate a new automation record for saving.
     * @returns an automation record for persisting.
     */
    private _generateAutomationRecord() {
        const automationRecord = new AutomationNoteMappingModel();
        if (!this.isUpdating) {
            automationRecord.id = this._workflowService.getIdFromServerInStore(this._store);
        } else {
            automationRecord.id = this.dialogOptions.existingMapping.id;
        }
        if (this.dialogOptions.type === AutomationActionType.CreateANote) {
            automationRecord.action = 'create';
            automationRecord.objectType = 'entry';
        } else if (this.dialogOptions.type === AutomationActionType.UpdateANote) {
            automationRecord.action = 'update';
            automationRecord.objectType = 'entry';
            automationRecord.dependencyObject = 'triggerProject';
        } else if (this.dialogOptions.type === AutomationActionType.CreateASidenote) {
            automationRecord.action = 'create';
            automationRecord.objectType = 'sideEntry';
            automationRecord.dependencyObject = 'triggerProject';
        } else {
            console.error('the automation action type did not currently supported' + this.dialogOptions.type);
        }
        automationRecord.objectTypeID = this.selectedNoteType.id;
        automationRecord.mapping = this.mappingMetadataList.map(it => AutomationMappingItemViewModel.toAutomationItem(it));
        return automationRecord;
    }

    /**
     * gets an array of mapping items including default metadata items and adhoc fields associated with the given note type.
     * @param noteType the given note type.
     * @returns a list including both default metadata items and adhoc fields associated with the given note type.
     */
    private _getNoteTypeRelatedMappingConfigs(noteType: EntryType): Observable<Array<AutomationMappingItemViewModel>> {
        return new Observable(o => {
            const mappingList = [];
            DEFAULT_METDATA_OF_NOTE.forEach(metaCfg => {
                const item = new AutomationMappingItemViewModel();
                if (this.dialogOptions.type === AutomationActionType.UpdateANote) {
                    item.isDeletable = true;
                } else {
                    item.isDeletable = metaCfg.isDeletable;
                }
                // Set isDeletable as true for creating a sidenote
                if (metaCfg.fieldType === 'entity' && metaCfg.fieldInTemplate === 'Entities') {
                    if (this.dialogOptions.type === AutomationActionType.CreateASidenote) {
                        item.isDeletable = true;
                    } else if (this.dialogOptions.type === AutomationActionType.CreateANote) {
                        item.isDeletable = false;
                    }
                }
                item.isPreloaded = metaCfg.isPreloaded;
                item.isMultipleSelection = metaCfg.isMultipleSelection;
                item.nameOfFieldInTemplate = metaCfg.nameOfFieldInTemplate;
                item.fieldInTemplate = metaCfg.fieldInTemplate;
                item.dataSourceForMultipleSelection = this._wrapDataSourceForMultipleSelection(metaCfg.applicableFieldTypes, metaCfg.isTaskSubmitterIncluded, metaCfg.applicableSystemVariables);
                item.dataSourceForSingleSelection = this._generateDataSourceForMappingItem(metaCfg.applicableFieldTypes, metaCfg.isTaskSubmitterIncluded, metaCfg.applicableSystemVariables);
                mappingList.push(item);
            });

            if (noteType) {
                this._openSubscriptions.push(
                    this._adhocService.getEntryAdhocsByEntryType(noteType.id).subscribe(
                        adhocFields => {
                            this.adhocFieldsOfCurrentNoteType = adhocFields;
                            mappingList.push(...this._getNewAdhocTemplateFields(adhocFields));
                            o.next(mappingList);
                            o.complete();
                        }, err => {
                            o.next(mappingList);
                            o.complete();
                        }
                    )
                );
            } else {
                o.next(mappingList);
                o.complete();
            }
        });
    }

    private _close(mappingModel: AutomationNoteMappingModel = null) {
        this.opened = false;
        const dialogResult = new DiagramNoteAutomationDialogResult();
        dialogResult.isConfirmed = !!mappingModel;
        if (mappingModel) {
            dialogResult.mappingResult = mappingModel;
        }
        dialogResult.isUpdate = this.dialogOptions.isUpdate;
        dialogResult.nodeId = this.dialogOptions.nodeId;
        this._dialogService.dialogClose$.next(dialogResult);
        this._openSubscriptions.forEach(sub => sub.unsubscribe());
        this._openSubscriptions = [];
        this._changeDetectorRef.detectChanges();
    }

    private _initNoteType(response: any) {
        if (response && response.entryTypes && response.entryTypes.length > 0) {
            this._allNoteTypes = response.entryTypes;
            ArrayHelperService.sort<EntryType>(this._allNoteTypes, 'name');
            this.filteredNoteTypeCandidates = this._allNoteTypes.slice();
        }
        this._afterNoteCandidatesInit();
    }

    /**
     * init the data for the controls
     */
    private _initCandidates() {
        this.allFieldControls = this._storeQueriery.queryBySelector(currentWorkflowAllFieldsSelector);
        this.submitterOfTaskList = this.dialogOptions.taskArray.map(kv => {
            return {
                key: OWNER_PREFIX.key + kv.key,
                value: OWNER_PREFIX.value + kv.value
            };
        });
        this._openSubscriptions.push(
            this._dialogService.getNoteTypes().subscribe(res => this._initNoteType(res))
        );
    }

    /**
     * init dialog properties
     */
    private _initDialogProperties() {
        this.diglogTitle = StringLiteralsPipe.translate('workflow.tamale_automation');
        if (this.dialogOptions.type === AutomationActionType.CreateASidenote) {
            this.diglogTitle = this.diglogTitle + ' - ' + StringLiteralsPipe.translate('workflow.create_a_sidenote');
        } else if (this.dialogOptions.type === AutomationActionType.UpdateANote) {
            this.diglogTitle = this.diglogTitle + ' - ' + StringLiteralsPipe.translate('workflow.update_a_note');
        } else if (this.dialogOptions.type === AutomationActionType.CreateANote) {
            this.diglogTitle = this.diglogTitle + ' - ' + StringLiteralsPipe.translate('workflow.create_a_note');
        } else {
            this.diglogTitle = this.diglogTitle + ' - ' + StringLiteralsPipe.translate('workflow.create_a_note');
        }
    }

    /**
     * reset the properties of the component
     */
    private _reset() {
        if (this._allNoteTypes.length > 0) {
            this.filteredNoteTypeCandidates = this._allNoteTypes.slice();
        }
        this.allFieldControls = [];
        this.mappingMetadataList = [];
        this.submitterOfTaskList = [];
        this.selectedNoteType = null;
        this.isSaveBtnClicked = false;
        this._isDialogPristine = true;
    }

    private _refreshTemplateFieldCandidates(noteType?: EntryType) {
        const newCandidates = [];
        const defaultMetadataOfNote = _lodash.cloneDeep(DEFAULT_METDATA_OF_NOTE);
        // Set isDeletable as true for creating a sidenote
        defaultMetadataOfNote.forEach(item => {
            if (item.fieldType === 'entity' && item.fieldInTemplate === 'Entities') {
                if (this.dialogOptions.type === AutomationActionType.CreateASidenote) {
                    item.isDeletable = true;
                } else if (this.dialogOptions.type === AutomationActionType.CreateANote) {
                    item.isDeletable = false;
                }
            }
            // all the fields can be deleted when updating a note
            if (this.dialogOptions.type === AutomationActionType.UpdateANote) {
                if (item.fieldInTemplate !== 'Submitter') {
                    item.isDeletable = true;
                }
            }
        });

        defaultMetadataOfNote.filter(it => it.isDeletable).forEach(metaCfg => {
            // pick up items not preloaded.
            if (!this.mappingMetadataList.find(it => it.fieldInTemplate === metaCfg.fieldInTemplate)) {
                const tF = new AutomationMappingItemViewModel();
                Object.assign(tF, metaCfg);
                newCandidates.push(tF);
            }
        });

        newCandidates.push(...this._getNewAdhocTemplateFields(this.adhocFieldsOfCurrentNoteType, this.mappingMetadataList));
        newCandidates.forEach(candidate => {
            const thisRef = this;
            candidate.click = item => {
                thisRef.onFieldCandidateSelected(item);
            };
        });
        this.templateFieldCandidates = newCandidates;
        this._changeDetectorRef.detectChanges();
    }

    /**
     * obtains those adhoc fields available as the candidates for the mapping list.
     * @param adhocFields all adhoc fields associated with current note type
     * @param viewModelList current mapping item list
     * @returns an adhoc canddiate list ready for adding to mapping list.
     */
    private _getNewAdhocTemplateFields(adhocFields: AdhocWorkflowField[], viewModelList?: AutomationMappingItemViewModel[]) {
        const adhocList = [];
        if (adhocFields) {
            adhocFields.forEach(adhocField => {
                if (!viewModelList || viewModelList.length === 0 || viewModelList.findIndex(vm => vm.fieldInTemplate === adhocField.attrDefID) < 0) {
                    const applicableFieldTypes = AdhocWorkflowField.getApplicableControlTypes(adhocField);
                    if (applicableFieldTypes && applicableFieldTypes.length > 0) {
                        // some adhoc field type like 'mdl' have no mapping workflow fields so do not put them into the candidate list.
                        const adhocItem = new AutomationMappingItemViewModel();
                        adhocItem.isPreloaded = false;
                        adhocItem.isDeletable = true;
                        adhocItem.isMultipleSelection = AdhocWorkflowField.isFieldMultipleValueInAutomation(adhocField);
                        adhocItem.applicableFieldTypes = applicableFieldTypes;
                        adhocItem.applicableSystemVariables = AdhocWorkflowField.getFieldSystemVariables(adhocField);
                        adhocItem.isTaskSubmitterIncluded = AdhocWorkflowField.isTaskSubmitterIncluded(adhocField);
                        adhocItem.dataSourceForSingleSelection = this._generateDataSourceForMappingItem(adhocItem.applicableFieldTypes, adhocItem.isTaskSubmitterIncluded, adhocItem.applicableSystemVariables);
                        adhocItem.dataSourceForMultipleSelection = this._wrapDataSourceForMultipleSelection(adhocItem.applicableFieldTypes, adhocItem.isTaskSubmitterIncluded, adhocItem.applicableSystemVariables);
                        adhocItem.fieldInTemplate = adhocField.attrDefID;
                        adhocItem.nameOfFieldInTemplate = adhocField.lableName;
                        adhocList.push(adhocItem);
                    }
                }
            });
        }

        return adhocList;
    }

    //#endregion
}
